declare global {
  interface Window {
    gtag?: (command: 'event', action: string, params?: GtagEventParams) => void;
  }
}

export type AnalyticsEventParams = Record<string, string | number | boolean | null | undefined>;
export type GtagEventParams = {
  [key: string]: string | number | boolean | null | undefined | (() => void);
};

type NodeCryptoModule = {
  createHash?: (algo: string) => {
    update: (data: string | ArrayBuffer | ArrayBufferView) => { digest: (enc: 'hex') => string };
  };
};

export function trackEvent(eventName: string, params?: GtagEventParams): void {
  if (typeof window === 'undefined') return;
  if (!window.gtag) return;
  window.gtag('event', eventName, params || {});
}

type DataLayerEventParams = {
  [key: string]: string | number | boolean | null | undefined | string[] | (() => void);
};

function pushDataLayerEvent(eventName: string, params: DataLayerEventParams): void {
  if (typeof window === 'undefined') return;
  const dl = (window as unknown as { dataLayer?: Array<Record<string, unknown>> }).dataLayer;
  if (!dl) {
    (window as unknown as { dataLayer: Array<Record<string, unknown>> }).dataLayer = [];
  }
  (window as unknown as { dataLayer: Array<Record<string, unknown>> }).dataLayer.push({
    event: eventName,
    ...params
  });
}

export async function hashSHA256(value: string): Promise<string> {
  try {
    if (typeof crypto !== 'undefined' && crypto.subtle) {
      const encoder = new TextEncoder();
      const data = encoder.encode(value);
      const hashBuffer = await crypto.subtle.digest('SHA-256', data);
      const bytes = Array.from(new Uint8Array(hashBuffer));
      return bytes.map((b) => b.toString(16).padStart(2, '0')).join('');
    }
  } catch {}
  try {
    // Node.js fallback for test environments
    const nodeCrypto = await import('crypto');
    const mod = nodeCrypto as unknown as NodeCryptoModule;
    if (typeof mod.createHash === 'function') {
      const h = mod.createHash('sha256');
      return h.update(value).digest('hex');
    }
  } catch {}
  return Array.from(value)
    .map((c) => c.charCodeAt(0).toString(16))
    .join('');
}

async function computeDocHash(docId: string): Promise<string> {
  const salt = process.env.NEXT_PUBLIC_DOC_HASH_SALT || '';
  const base = `${salt}:${(docId || '').trim()}`;
  return hashSHA256(base);
}

export function maskOrderId(id: string, visible: number = 4): string {
  const v = Math.max(0, Math.min(visible, id.length));
  const tail = id.slice(-v);
  const masked = '*'.repeat(Math.max(0, id.length - v));
  return `${masked}${tail}`;
}

export function trackButtonClick(
  params: {
    id: string;
    labelKey?: string;
    labelText?: string;
    section?: string;
    orderId?: string;
    route?: string;
  },
  onSent?: () => void
): void {
  if (typeof window === 'undefined') return;
  const payload: GtagEventParams = {
    content_type: 'button',
    content_id: params.id,
    content_name: params.labelKey || params.labelText,
    section: params.section,
    order_id: params.orderId,
    route: params.route,
    event_callback: onSent,
    event_timeout: 2000
  };
  if (!window.gtag) {
    onSent?.();
    return;
  }
  window.gtag('event', 'select_content', payload);
}

export function trackDeliveryPlanSelection(
  params: {
    planCode: string;
    planName?: string;
    orderId?: string;
    storeId?: string | null;
    organizationId?: string | null;
    companyId?: string | null;
    skus?: string[];
    section?: string;
  },
  onSent?: () => void
): void {
  if (typeof window === 'undefined') return;
  const payload: GtagEventParams = {
    content_type: 'delivery_plan',
    content_id: params.planCode,
    content_name: params.planName,
    order_id: params.orderId,
    store_id: params.storeId ?? undefined,
    organization_id: params.organizationId ?? undefined,
    company_id: params.companyId ?? undefined,
    item_skus: params.skus?.join(',') ?? undefined,
    section: params.section,
    event_callback: onSent,
    event_timeout: 2000
  };
  if (!window.gtag) {
    onSent?.();
    return;
  }
  window.gtag('event', 'select_content', payload);
}

export async function trackSearchClick(params: {
  searchType: 'factura' | 'pedido' | 'pick-slip' | 'pickslip';
  searchValue: string;
  resultCount: number;
}): Promise<void> {
  pushDataLayerEvent('traky.search.click', {
    searchType: params.searchType,
    searchHash: params.searchValue,
    resultCount: params.resultCount
  });
}

export function trackOrderDetailOpen(params: {
  orderIdMasked: string;
  deliveryPlanType?: string | null;
}): void {
  pushDataLayerEvent('traky.order.detail.open', {
    orderIdMasked: params.orderIdMasked,
    deliveryPlanType: params.deliveryPlanType ?? undefined
  });
}

export function trackFilterApply(params: {
  filterType: string;
  selectedValues: string[] | string | null | undefined;
}): void {
  pushDataLayerEvent('traky.filter.apply', {
    filterType: params.filterType,
    selectedValues: Array.isArray(params.selectedValues)
      ? params.selectedValues
      : (params.selectedValues ?? undefined)
  });
}

export function trackPaginationChange(params: { pageNumber: number }): void {
  pushDataLayerEvent('traky.pagination.change', {
    pageNumber: params.pageNumber
  });
}

export function trackTooltipHover(params: { planCode: string }): void {
  pushDataLayerEvent('traky.tooltip.hover', {
    planCode: params.planCode
  });
}

export async function trackDocumentLookup(params: {
  docId: string;
  lookupResult: 'FOUND' | 'NOT_FOUND' | 'ERROR';
  docType?: 'factura' | 'pedido';
}): Promise<void> {
  pushDataLayerEvent('traky.document.lookup', {
    doc_hash: params.docId,
    lookup_result: params.lookupResult,
    doc_type: params.docType
  });

  if (
    typeof window !== 'undefined' &&
    typeof window.gtag === 'function' &&
    !(window as unknown as { google_tag_manager?: unknown }).google_tag_manager
  ) {
    try {
      window.gtag('event', 'traky.document.lookup', {
        doc_hash: params.docId,
        lookup_result: params.lookupResult,
        doc_type: params.docType
      });
    } catch {}
  }
}

export function trackClickSearch(params: {
  search_type: 'INVOICE' | 'WEB_ORDER';
  search_input_length: number;
  has_results: boolean;
  result_count: number;
}): void {
  pushDataLayerEvent('traky_click_search', {
    search_type: params.search_type,
    search_input_length: params.search_input_length,
    has_results: params.has_results,
    result_count: params.result_count
  });

  if (
    typeof window !== 'undefined' &&
    typeof window.gtag === 'function' &&
    !(window as unknown as { google_tag_manager?: unknown }).google_tag_manager
  ) {
    try {
      window.gtag('event', 'traky_click_search', {
        search_type: params.search_type,
        search_input_length: params.search_input_length,
        has_results: params.has_results,
        result_count: params.result_count
      });
    } catch {}
  }
}

export function trackClickMoreSearchOptions(params: {
  search_type_selected?: 'INVOICE' | 'WEB_ORDER';
  expanded: boolean;
}): void {
  pushDataLayerEvent('traky_click_more_search_options', {
    search_type_selected: params.search_type_selected,
    expanded: params.expanded
  });

  if (
    typeof window !== 'undefined' &&
    typeof window.gtag === 'function' &&
    !(window as unknown as { google_tag_manager?: unknown }).google_tag_manager
  ) {
    try {
      window.gtag('event', 'traky_click_more_search_options', {
        search_type_selected: params.search_type_selected,
        expanded: params.expanded
      });
    } catch {}
  }
}

export function trackSearchTypeChange(params: {
  previous_type?: 'factura' | 'pickslip' | 'pedido' | null;
  new_type: 'factura' | 'pickslip' | 'pedido';
}): void {
  pushDataLayerEvent('traky_search_type_change', {
    previous_type: params.previous_type ?? undefined,
    new_type: params.new_type
  });

  if (
    typeof window !== 'undefined' &&
    typeof window.gtag === 'function' &&
    !(window as unknown as { google_tag_manager?: unknown }).google_tag_manager
  ) {
    try {
      window.gtag('event', 'traky_search_type_change', {
        previous_type: params.previous_type ?? undefined,
        new_type: params.new_type
      });
    } catch {}
  }
}

export async function trackClickReturnIcon(params: {
  docId: string;
  docType: 'factura' | 'pedido';
  hasReturn: boolean;
  status?: string | null;
}): Promise<void> {
  pushDataLayerEvent('traky_click_return_icon', {
    doc_hash: params.docId,
    doc_type: params.docType,
    has_return: params.hasReturn,
    status: params.status ?? undefined
  });

  if (
    typeof window !== 'undefined' &&
    typeof window.gtag === 'function' &&
    !(window as unknown as { google_tag_manager?: unknown }).google_tag_manager
  ) {
    try {
      window.gtag('event', 'traky_click_return_icon', {
        doc_hash: params.docId,
        doc_type: params.docType,
        has_return: params.hasReturn,
        status: params.status ?? undefined
      });
    } catch {}
  }
}

export async function trackClickViewDetail(params: {
  docId: string;
  docType: 'factura' | 'pedido';
  status?: string | null;
  delivery_plan?: string | null;
}): Promise<void> {
  pushDataLayerEvent('traky_click_view_detail', {
    doc_hash: params.docId,
    doc_type: params.docType,
    status: params.status ?? undefined,
    delivery_plan: params.delivery_plan ?? undefined
  });

  if (
    typeof window !== 'undefined' &&
    typeof window.gtag === 'function' &&
    !(window as unknown as { google_tag_manager?: unknown }).google_tag_manager
  ) {
    try {
      window.gtag('event', 'traky_click_view_detail', {
        doc_hash: params.docId,
        doc_type: params.docType,
        status: params.status ?? undefined,
        delivery_plan: params.delivery_plan ?? undefined
      });
    } catch {}
  }
}

export async function trackDeliveryPlanChange(params: {
  docId: string;
  previous_plan?: string | null;
  new_plan: string;
  change_type: 'USER_ACTION' | 'SYSTEM_UPDATE';
}): Promise<void> {
  pushDataLayerEvent('traky_delivery_plan_change', {
    doc_hash: params.docId,
    previous_plan: params.previous_plan ?? undefined,
    new_plan: params.new_plan,
    change_type: params.change_type
  });

  if (
    typeof window !== 'undefined' &&
    typeof window.gtag === 'function' &&
    !(window as unknown as { google_tag_manager?: unknown }).google_tag_manager
  ) {
    try {
      window.gtag('event', 'traky_delivery_plan_change', {
        doc_hash: params.docId,
        previous_plan: params.previous_plan ?? undefined,
        new_plan: params.new_plan,
        change_type: params.change_type
      });
    } catch {}
  }
}

export async function trackClickInvoice(params: {
  invoice_id: string;
  doc_type: 'invoice' | 'pickslip' | 'web_order';
  source_screen: 'search' | 'dashboard';
  result_position?: number | null;
  result_count?: number | null;
  status?: string | null;
  delivery_plan?: string | null;
}): Promise<void> {
  pushDataLayerEvent('traky_click_invoice', {
    invoice_id: params.invoice_id,
    doc_type: params.doc_type,
    source_screen: params.source_screen,
    result_position: params.result_position ?? undefined,
    result_count: params.result_count ?? undefined,
    status: params.status ?? undefined,
    delivery_plan: params.delivery_plan ?? undefined
  });

  if (
    typeof window !== 'undefined' &&
    typeof window.gtag === 'function' &&
    !(window as unknown as { google_tag_manager?: unknown }).google_tag_manager
  ) {
    try {
      window.gtag('event', 'traky_click_invoice', {
        invoice_id: params.invoice_id,
        doc_type: params.doc_type,
        source_screen: params.source_screen,
        result_position: params.result_position ?? undefined,
        result_count: params.result_count ?? undefined,
        status: params.status ?? undefined,
        delivery_plan: params.delivery_plan ?? undefined
      });
    } catch {}
  }
}

export function trackDashboardStatusFilter(params: {
  status_selected: string;
  previous_filter?: string | null;
  result_count?: number | null;
}): void {
  pushDataLayerEvent('traky_dashboard_status_filter', {
    status_selected: params.status_selected,
    previous_filter: params.previous_filter ?? undefined,
    result_count: params.result_count ?? undefined
  });

  if (
    typeof window !== 'undefined' &&
    typeof window.gtag === 'function' &&
    !(window as unknown as { google_tag_manager?: unknown }).google_tag_manager
  ) {
    try {
      window.gtag('event', 'traky_dashboard_status_filter', {
        status_selected: params.status_selected,
        previous_filter: params.previous_filter ?? undefined,
        result_count: params.result_count ?? undefined
      });
    } catch {}
  }
}
